import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client';

/**
 * Простая лента сообщений преподаватель↔ученик.
 * mode: 'teacher' | 'student'
 */
export default function StudentMessageThread({
  mode,
  studentId,
  teacherId,
  studentName,
  pollMs = 8000,
}) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const lastIdRef = useRef(0);

  const endpoint = mode === 'teacher'
    ? `/messaging/teacher/students/${studentId}/messages/`
    : `/messaging/me/threads/${teacherId}/`;

  const load = useCallback(async ({ silent = false, afterId = null } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const params = {};
      if (mode === 'student' && studentId) params.student_id = studentId;
      if (afterId) params.after_id = afterId;
      const res = await api.get(endpoint, { params });
      const items = res.data?.messages || [];
      if (afterId) {
        if (items.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            return [...prev, ...items.filter((m) => !seen.has(m.id))];
          });
          lastIdRef.current = Math.max(lastIdRef.current, ...items.map((m) => m.id));
        }
      } else {
        setMessages(items);
        lastIdRef.current = items.length ? items[items.length - 1].id : 0;
      }
    } catch (err) {
      if (!silent) {
        setError(err.response?.data?.detail || 'Не удалось загрузить сообщения');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [endpoint, mode, studentId]);

  useEffect(() => {
    lastIdRef.current = 0;
    load();
  }, [load]);

  useEffect(() => {
    if (!pollMs) return undefined;
    const id = setInterval(() => {
      if (lastIdRef.current) load({ silent: true, afterId: lastIdRef.current });
      else load({ silent: true });
    }, pollMs);
    return () => clearInterval(id);
  }, [load, pollMs]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  async function handleSend(e) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      const payload = { body: text };
      if (mode === 'student' && studentId) payload.student_id = studentId;
      const res = await api.post(endpoint, payload);
      setMessages((prev) => [...prev, res.data]);
      lastIdRef.current = res.data.id;
      setBody('');
    } catch (err) {
      setError(err.response?.data?.body || err.response?.data?.detail || 'Не отправилось');
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="ops-thread">
      <h2 className="ops-thread-title">
        {mode === 'teacher'
          ? `Сообщения${studentName ? `: ${studentName}` : ''}`
          : 'Переписка с преподавателем'}
      </h2>
      <p className="muted ops-thread-hint">
        Организационные вопросы и короткие ответы по учёбе. Это не мессенджер:
        ссылки на урок и переносы лучше через кнопки в расписании.
      </p>
      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p className="muted">Загрузка…</p>
      ) : (
        <div className="ops-thread-list" ref={listRef}>
          {messages.length === 0 ? (
            <p className="muted">Пока пусто — напишите первое сообщение.</p>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={`ops-bubble ${m.is_mine ? 'ops-bubble--mine' : 'ops-bubble--theirs'}`}
              >
                <div className="ops-bubble-meta">
                  {m.author_name}
                  {' · '}
                  {new Date(m.created_at).toLocaleString('ru-RU', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                <div className="ops-bubble-body">{m.body}</div>
              </div>
            ))
          )}
        </div>
      )}
      <form className="ops-thread-compose" onSubmit={handleSend}>
        <textarea
          className="form-input"
          rows={2}
          placeholder="Текст сообщения…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
        />
        <button type="submit" className="btn btn-primary" disabled={sending || !body.trim()}>
          {sending ? '…' : 'Отправить'}
        </button>
      </form>
    </section>
  );
}
